/**
 * RecordLinkPicker.jsx: link a to-do to one Job, Quote, Invoice or Person.
 *
 * One Autocomplete grouped Jobs / Quotes / Invoices / People, built from the list
 * hooks the rest of the portal uses. The server scopes each list to what the
 * viewer may see, with the same rules it checks when the to-do is saved, so the
 * picker never offers a record the save would refuse. A type shows only when the
 * role can see it (crew never fire a 403), and the lists load when the picker is
 * first opened. At most 8 matches per type. Never shows amounts or emails.
 *
 * value / onChange: { type, id, label } | null.
 */
import { useMemo, useState } from 'react';
import { Autocomplete, TextField, Box, Typography } from '@mui/material';
import { useRBAC } from '../../hooks/useRBAC';
import { useSettings } from '../../hooks/useSettings';
import { useJobs } from '../../hooks/useJobs';
import { useQuotes } from '../../hooks/useQuotes';
import { useInvoices } from '../../hooks/useInvoices';
import { useContacts } from '../../hooks/useContacts';

const MAX_PER_TYPE = 8;
const GROUPS = [
  ['job', 'Jobs'],
  ['quote', 'Quotes'],
  ['invoice', 'Invoices'],
  ['contact', 'People'],
];
const GROUP_LABEL = Object.fromEntries(GROUPS);
// Clients first, then crew, leads, and the long prospect list last.
const CONTACT_ORDER = { client: 0, crew: 1, lead: 2, prospect: 3 };

const newestFirst = (a, b) => String(b.createdAt || '').localeCompare(String(a.createdAt || ''));
const sameLink = (a, b) => !!a && !!b && a.type === b.type && a.id === b.id;

export default function RecordLinkPicker({ value, onChange, disabled, label = 'Link to' }) {
  const { hasAccess } = useRBAC();
  const { data: settings } = useSettings();
  const [armed, setArmed] = useState(false);
  const can = {
    job: hasAccess('jobs', 'view_assigned', settings),
    quote: hasAccess('quotes', 'view_assigned', settings),
    invoice: hasAccess('invoices', 'view_assigned', settings),
    contact: hasAccess('crm', 'view_no_financials', settings),
  };
  const jobsQ = useJobs({ enabled: armed && can.job });
  const quotesQ = useQuotes({ enabled: armed && can.quote });
  const invoicesQ = useInvoices({ enabled: armed && can.invoice });
  const contactsQ = useContacts({ enabled: armed && can.contact });

  const options = useMemo(() => {
    const out = [];
    if (can.job) {
      out.push(...[...(jobsQ.data || [])].sort(newestFirst).map((j) => ({
        type: 'job', id: j.id, label: j.title || j.clientName || '(Untitled)', sublabel: j.title ? j.clientName || '' : '',
      })));
    }
    if (can.quote) {
      out.push(...(quotesQ.data || []).filter((q) => q.status !== 'superseded').sort(newestFirst).map((q) => ({
        type: 'quote', id: q.id, label: q.quoteNumber || '(No number)', sublabel: [q.clientName, q.status].filter(Boolean).join(' · '),
      })));
    }
    if (can.invoice) {
      out.push(...[...(invoicesQ.data || [])].sort(newestFirst).map((i) => ({
        type: 'invoice', id: i.id, label: i.number || '(No number)', sublabel: i.client || '',
      })));
    }
    if (can.contact) {
      out.push(...[...(contactsQ.data || [])]
        .sort((a, b) => (CONTACT_ORDER[a.type] ?? 4) - (CONTACT_ORDER[b.type] ?? 4)
          || String(a.name || '').localeCompare(String(b.name || '')))
        .map((c) => ({ type: 'contact', id: c.id, label: c.name || '(No name)', sublabel: c.company || '' })));
    }
    // The saved link stays a valid value before the lists load (or when its record is gone).
    if (value?.type && !out.some((o) => sameLink(o, value))) {
      out.push({ type: value.type, id: value.id, label: value.label || '(No name)', sublabel: value.sublabel || '' });
    }
    return out.map((o) => ({ ...o, group: GROUP_LABEL[o.type] || 'Other' }));
  }, [can.job, can.quote, can.invoice, can.contact, jobsQ.data, quotesQ.data, invoicesQ.data, contactsQ.data, value]);

  const filterOptions = (opts, { inputValue }) => {
    const q = inputValue.trim().toLowerCase();
    return GROUPS.flatMap(([type]) => opts
      .filter((o) => o.type === type && (!q || `${o.label} ${o.sublabel}`.toLowerCase().includes(q)))
      .slice(0, MAX_PER_TYPE));
  };

  const loading = armed && [jobsQ, quotesQ, invoicesQ, contactsQ].some((qr) => qr.isFetching && !qr.data);
  const selected = value?.type ? options.find((o) => sameLink(o, value)) || null : null;

  return (
    <Autocomplete
      data-track="todos.dialog.link"
      size="small"
      disabled={disabled}
      options={options}
      value={selected}
      onOpen={() => setArmed(true)}
      onChange={(_e, opt) => onChange(opt ? { type: opt.type, id: opt.id, label: opt.label } : null)}
      filterOptions={filterOptions}
      groupBy={(o) => o.group}
      getOptionLabel={(o) => o?.label || ''}
      getOptionKey={(o) => `${o.type}:${o.id}`}
      isOptionEqualToValue={sameLink}
      sx={{ '& .MuiInputBase-root': { minHeight: 44 } }}
      loading={loading}
      loadingText="Loading..."
      noOptionsText="No matches."
      renderOption={(props, o) => {
        const { key: _key, ...rest } = props;
        return (
          <Box component="li" key={`${o.type}:${o.id}`} {...rest} sx={{ display: 'block !important', minHeight: 44 }}>
            <Typography variant="body2" noWrap>{o.label}</Typography>
            {o.sublabel && (
              <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>{o.sublabel}</Typography>
            )}
          </Box>
        );
      }}
      renderInput={(params) => <TextField {...params} label={label} placeholder="Job, quote, invoice or person" />}
    />
  );
}
